import { AxiosError } from 'axios';
import { useIntl } from 'react-intl';
import * as z from 'zod';

import { toast } from 'src/hooks/UseToast';
import { SmartParkError } from 'src/registry/types/axios';
import { generalErrorMessages, generalValidationMessages } from 'src/registry/utils/messages';

import type { FieldPath, FieldValues, UseFormSetError } from 'react-hook-form';

const CUSTOM_ERROR_PREFIX = 'custom-error:';

type SingleAttributeError = { detail: string; attr: string; code: string };
function useFormatCustomError() {
    const { formatMessage } = useIntl();
    return (error: SingleAttributeError) => {
        if (error.attr) {
            const id = `${CUSTOM_ERROR_PREFIX}${error.attr}`;
            return formatMessage({ id, defaultMessage: error.detail });
        }

        const id = `${CUSTOM_ERROR_PREFIX}${error.code}`;
        return formatMessage({ id, defaultMessage: error.detail });
    };
}

function useMapError() {
    const format = useFormatCustomError();
    const { formatMessage } = useIntl();
    return <TData extends FieldValues>(
        smartParkError: SmartParkError | undefined,
        setError: UseFormSetError<TData>,
        inlineNoAttr: boolean,
    ) => {
        if (!smartParkError) return;
        const rootMessages: string[] = [];
        smartParkError.errors.forEach((error) => {
            const errorMessage = format(error as SingleAttributeError);
            if (error.attr) {
                setError(error.attr as FieldPath<TData>, {
                    type: 'server',
                    message: errorMessage,
                });
                return;
            }
            if (inlineNoAttr) {
                rootMessages.push(errorMessage);
            } else {
                toast({
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: errorMessage,
                    variant: 'destructive',
                });
            }
        });
        if (rootMessages.length > 0) {
            setError('root', {
                type: 'server',
                message: rootMessages.join('\n'),
            });
        }
    };
}
function useOnError() {
    const mapErrors = useMapError();
    const { formatMessage } = useIntl();
    return <TData extends FieldValues>(
        error: AxiosError<SmartParkError>,
        setError: UseFormSetError<TData>,
        options?: { inlineErrorStatuses?: number[] },
    ) => {
        const inlineErrorStatuses = options?.inlineErrorStatuses ?? [400];
        const status = error?.response?.status;
        const isInline = status != null && inlineErrorStatuses.includes(status);

        // 400 responses carry the SmartParkError.errors[] shape we map per-entry.
        if (status === 400) {
            mapErrors(error?.response?.data, setError, isInline);
            return;
        }

        // Other statuses: a single form-level message.
        const errorDetails = error.response?.data?.errors?.[0]?.detail;
        const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);
        if (isInline) {
            setError('root', { type: 'server', message: errorMessage });
        } else {
            toast({
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: errorMessage,
                variant: 'destructive',
            });
        }
    };
}

const useZodCustomErrorMap = () => {
    const { formatMessage } = useIntl();

    z.config({
        customError: (error) => {
            switch (error.code) {
                case 'too_small':
                    if (error.origin === 'array') {
                        return { message: formatMessage(generalValidationMessages.fieldRequired) };
                    }
                    if (error.origin === 'string' && error.minimum > 1) {
                        return {
                            message: formatMessage(generalValidationMessages.fieldInvalidMinLength, {
                                min: error.minimum as number,
                            }),
                        };
                    }

                    if (error.origin === 'number' && (error.minimum >= 1 || error.minimum < 0)) {
                        return {
                            message: formatMessage(generalValidationMessages.fieldInvalidMinValue, {
                                min: error.minimum as number,
                            }),
                        };
                    }
                    return { message: formatMessage(generalValidationMessages.fieldRequired) };
                case 'too_big':
                    if (error.origin === 'string') {
                        return {
                            message: formatMessage(generalValidationMessages.fieldInvalidMaxLength, {
                                max: error.maximum as number,
                            }),
                        };
                    }
                    if (error.origin === 'number') {
                        return {
                            message: formatMessage(generalValidationMessages.fieldInvalidMaxValue, {
                                max: error.maximum as number,
                            }),
                        };
                    }
                    return error.message;
                case 'invalid_type':
                    return { message: formatMessage(generalValidationMessages.fieldRequired) };
                case 'invalid_value':
                    return { message: formatMessage(generalValidationMessages.fieldRequired) };
                case 'invalid_format':
                    return { message: formatMessage(generalValidationMessages.fieldInvalidEmail) };
                default:
                    return error.message;
            }
        },
    });
};

export { useOnError, useZodCustomErrorMap, useFormatCustomError };
